import { format, isValid, parseISO, startOfYear, subDays } from 'date-fns';
import { ReportId, ReportIds } from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import { MasterDatasetType } from 'ag-common-lib/lib/models/reporting/master-dataset.model';

export interface ProductionReportDefaultDateRange {
  startDate: string;
  endDate: string;
}

const PAID_REPORT_IDS = new Set<ReportId>([ReportIds.mgaPaid, ReportIds.personalPaid, ReportIds.rmdPaid]);
const SUBMIT_REPORT_IDS = new Set<ReportId>([ReportIds.mgaSubmit, ReportIds.personalSubmit, ReportIds.rmdSubmit]);

export function resolveProductionReportDatasetType(reportId: ReportId | string): MasterDatasetType | null {
  if (PAID_REPORT_IDS.has(reportId as ReportId)) {
    return MasterDatasetType.Paid;
  }

  if (SUBMIT_REPORT_IDS.has(reportId as ReportId)) {
    return MasterDatasetType.Submit;
  }

  return null;
}

export function buildProductionReportDefaultDateRange(
  reportId: ReportId | string,
  uploadDate?: Date | string,
): ProductionReportDefaultDateRange | null {
  const normalizedUploadDate = uploadDate ? normalizeDateInput(uploadDate) : null;
  if (!normalizedUploadDate) {
    return null;
  }

  if (PAID_REPORT_IDS.has(reportId as ReportId)) {
    return buildProductionReportYearToDateRange(normalizedUploadDate);
  }

  if (!SUBMIT_REPORT_IDS.has(reportId as ReportId)) {
    return null;
  }

  return {
    startDate: toIsoDate(subDays(normalizedUploadDate, 6)),
    endDate: toIsoDate(normalizedUploadDate),
  };
}

export function buildProductionReportDateRangeFromEndDate(
  reportId: ReportId | string,
  endDate: Date | string,
): ProductionReportDefaultDateRange | null {
  const normalizedEndDate = normalizeDateInput(endDate);
  if (!normalizedEndDate) {
    return null;
  }

  if (PAID_REPORT_IDS.has(reportId as ReportId)) {
    return buildProductionReportYearToDateRange(normalizedEndDate);
  }

  return {
    startDate: toIsoDate(subDays(normalizedEndDate, 6)),
    endDate: toIsoDate(normalizedEndDate),
  };
}

function buildProductionReportYearToDateRange(endDate: Date): ProductionReportDefaultDateRange {
  return {
    startDate: toIsoDate(startOfYear(endDate)),
    endDate: toIsoDate(endDate),
  };
}

function toIsoDate(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function normalizeDateInput(value: Date | string): Date | null {
  const date = value instanceof Date ? value : parseISO(value);
  return isValid(date) ? date : null;
}
